import { ISessionResponse, ITokenResponse } from './dto';
import { getTokenExpirationMs } from './getTokenExpirationMs';
import { IJWTPayload } from './types';

interface CachedToken {
  token: string;
  expiresAt: number;
}

interface CachedSession {
  data: IJWTPayload;
  expiresAt: number;
}

/**
 * Outcome of a session renewal. `ended` means the refresh token was rejected
 * and the session is over; `transient` means the renewal could not complete
 * (network, 5xx, rate limit) and says nothing about the session.
 */
export type RefreshResult =
  { kind: 'token'; token: string } | { kind: 'ended' } | { kind: 'transient' };

/**
 * Client-side session management infrastructure
 * Provides secure access to session data and tokens via API routes
 * Implements in-memory caching to avoid redundant API calls
 */
class ClientSessionManager {
  private static instance: ClientSessionManager;
  private tokenCache: CachedToken | null = null;
  private sessionCache: CachedSession | null = null;
  private tokenPromise: Promise<string | null> | null = null;
  private sessionPromise: Promise<IJWTPayload | null> | null = null;
  private refreshPromise: Promise<RefreshResult> | null = null;
  private readonly CACHE_BUFFER_MS = 60000; // 1 minute before expiration

  private constructor() {}

  public static getInstance(): ClientSessionManager {
    if (!ClientSessionManager.instance) {
      ClientSessionManager.instance = new ClientSessionManager();
    }
    return ClientSessionManager.instance;
  }

  /**
   * Clear all cached data
   */
  clearCache(): void {
    this.tokenCache = null;
    this.sessionCache = null;
    this.tokenPromise = null;
    this.sessionPromise = null;
    this.refreshPromise = null;
  }

  /**
   * Check if cached token is still valid
   */
  private isTokenCacheValid(): boolean {
    if (!this.tokenCache) return false;
    return Date.now() < this.tokenCache.expiresAt;
  }

  /**
   * Check if cached session is still valid
   */
  private isSessionCacheValid(): boolean {
    if (!this.sessionCache) return false;
    return Date.now() < this.sessionCache.expiresAt;
  }

  /**
   * Get expiration time from JWT token
   */
  private getTokenExpiration(token: string): number {
    const expiration = getTokenExpirationMs(token, this.CACHE_BUFFER_MS);

    // Return expiration or default to 5 minutes if we can't decode
    return expiration ?? Date.now() + 300000;
  }

  /**
   * Fetch access token from API
   */
  private async fetchAccessToken(): Promise<string | null> {
    try {
      const response = await fetch('/api/auth/token', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.clearCache();
          return null;
        }
        throw new Error(`Token API error: ${response.status}`);
      }

      const data: ITokenResponse = await response.json();
      const token = data.accessToken;

      // Cache the token with expiration
      if (token) {
        this.tokenCache = {
          token,
          expiresAt: this.getTokenExpiration(token),
        };
      }

      return token;
    } catch (error) {
      console.error('Error fetching access token:', error);
      this.clearCache();
      return null;
    }
  }

  /**
   * Fetch session data from API
   */
  private async fetchSessionData(): Promise<IJWTPayload | null> {
    try {
      const response = await fetch('/api/auth/session', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.clearCache();
          return null;
        }
        throw new Error(`Session API error: ${response.status}`);
      }

      const responseData: ISessionResponse = await response.json();
      const sessionData = responseData.userData;

      // Cache session data (use same expiration as token)
      if (sessionData) {
        this.sessionCache = {
          data: sessionData,
          expiresAt: Date.now() + 3600000, // 1 hour default
        };
      }

      return sessionData;
    } catch (error) {
      console.error('Error fetching session data:', error);
      this.clearCache();
      return null;
    }
  }

  /**
   * Get user session data from cache or API
   */
  async getSessionData(): Promise<IJWTPayload | null> {
    // Return cached data if valid
    if (this.isSessionCacheValid() && this.sessionCache) {
      return this.sessionCache.data;
    }

    // If there's already a pending request, wait for it (promise deduplication)
    if (this.sessionPromise) {
      return this.sessionPromise;
    }

    // Create new request
    this.sessionPromise = this.fetchSessionData();

    try {
      const result = await this.sessionPromise;
      return result;
    } finally {
      this.sessionPromise = null;
    }
  }

  /**
   * Get access token from cache or API
   */
  async getAccessToken(): Promise<string | null> {
    // Return cached token if valid
    if (this.isTokenCacheValid() && this.tokenCache) {
      return this.tokenCache.token;
    }

    // If there's already a pending request, wait for it (promise deduplication)
    if (this.tokenPromise) {
      return this.tokenPromise;
    }

    // Create new request
    this.tokenPromise = this.fetchAccessToken();

    try {
      const result = await this.tokenPromise;
      return result;
    } finally {
      this.tokenPromise = null;
    }
  }

  /**
   * Exchange the (HttpOnly) refresh token for a fresh access token via the
   * /api/auth/refresh route. The route rotates and re-issues the cookie; here
   * we only update the in-memory access-token cache. Single-flight so a burst
   * of 401s triggers exactly one refresh.
   */
  async refreshAccessToken(): Promise<RefreshResult> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.fetchRefreshedToken();

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  // Only the route's 401 ends the session; anything else leaves the cache and
  // the cookie alone so the next request can try the renewal again.
  private async fetchRefreshedToken(): Promise<RefreshResult> {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401) {
        this.clearCache();
        return { kind: 'ended' };
      }
      if (!response.ok) {
        return { kind: 'transient' };
      }

      const data: ITokenResponse = await response.json();
      const token = data.accessToken;
      if (!token) {
        return { kind: 'transient' };
      }

      this.tokenCache = {
        token,
        expiresAt: this.getTokenExpiration(token),
      };
      return { kind: 'token', token };
    } catch (error) {
      console.error('Error refreshing access token:', error);
      return { kind: 'transient' };
    }
  }

  /**
   * Check if session is valid (has both session data and token)
   */
  async isSessionValid(): Promise<boolean> {
    try {
      const [sessionData, accessToken] = await Promise.all([
        this.getSessionData(),
        this.getAccessToken(),
      ]);

      return !!(sessionData && accessToken);
    } catch (error) {
      console.error('Error checking session validity:', error);
      return false;
    }
  }

  /**
   * Force refresh of cached data
   */
  async refreshCache(): Promise<void> {
    this.clearCache();
    await Promise.all([this.getSessionData(), this.getAccessToken()]);
  }
}

// Export singleton instance and individual functions for convenience
export const clientSessionManager = ClientSessionManager.getInstance();

export const getClientSessionData = () => clientSessionManager.getSessionData();
export const getClientAccessToken = () => clientSessionManager.getAccessToken();
export const refreshClientAccessToken = () => clientSessionManager.refreshAccessToken();
export const isClientSessionValid = () => clientSessionManager.isSessionValid();
export const clearClientSessionCache = () => clientSessionManager.clearCache();
export const refreshClientSessionCache = () => clientSessionManager.refreshCache();
